import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Eye, EyeOff } from 'lucide-react';

interface ModelSetting {
  id: number;
  provider_name: string;
  model_id: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  config: Record<string, any>;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface Props {
  open: boolean;
  mode: 'add' | 'edit';
  providerName: string;
  model?: ModelSetting;
  onSave: (data: Record<string, any>) => void;
  onClose: () => void;
}

const ModelEditModal: React.FC<Props> = ({
  open, mode, providerName, model, onSave, onClose,
}) => {
  const [modelId, setModelId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [provider, setProvider] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);

  useEffect(() => {
    if (mode === 'edit' && model) {
      setModelId(model.model_id);
      setDisplayName(model.display_name);
      setEnabled(model.enabled);
      setIsDefault(model.is_default);
      setProvider(model.config?.provider || '');
      setApiKey(model.config?.api_key || '');
      setSortOrder(model.sort_order);
    } else {
      setModelId('');
      setDisplayName('');
      setEnabled(true);
      setIsDefault(false);
      setProvider('');
      setApiKey('');
      setSortOrder(0);
    }
  }, [mode, model, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Record<string, any> = {
      display_name: displayName || modelId,
      enabled,
      is_default: isDefault,
      sort_order: sortOrder,
    };

    if (mode === 'add') {
      data.model_id = modelId;
    }

    // external provider에서는 config에 provider + api_key 추가
    if (providerName === 'external') {
      const config: Record<string, any> = {};
      if (provider) config.provider = provider;
      if (apiKey) config.api_key = apiKey;
      if (Object.keys(config).length > 0) data.config = config;
    }

    onSave(data);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? '모델 추가' : '모델 편집'} - {providerName}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="model-id">모델 ID</Label>
            <Input
              id="model-id"
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              placeholder="예: gpt-4o-mini, exaone3.5:2.4b"
              disabled={mode === 'edit'}
              required
            />
          </div>
          <div>
            <Label htmlFor="display-name">표시 이름</Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="모델 표시 이름 (비워두면 모델 ID 사용)"
            />
          </div>

          {providerName === 'external' && (
            <>
              <div>
                <Label htmlFor="provider-type">Provider 유형</Label>
                <select
                  id="provider-type"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm"
                >
                  <option value="">선택하세요</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="google">Google</option>
                </select>
              </div>
              <div>
                <Label htmlFor="api-key">API Key (모델별)</Label>
                <div className="relative">
                  <Input
                    id="api-key"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="비워두면 Provider 글로벌 키 → 환경변수 순으로 사용"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  우선순위: 모델별 키 &gt; Provider 글로벌 키 &gt; 환경변수
                </p>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="sort-order">정렬 순서</Label>
            <Input
              id="sort-order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">활성화</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">기본 모델로 설정</span>
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>취소</Button>
            <Button type="submit">{mode === 'add' ? '추가' : '저장'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ModelEditModal;
